import { ref } from "vue";
import { AwsRum } from "aws-rum-web";
import { restClient } from "@/lib/raftApi";
import { useMemoize } from "@vueuse/core";
import axios from "axios";
import { useDrupalSettings } from "./useDrupalSettings";

/**
 * useCloudWatchRum - Vue composable for integrating AWS CloudWatch RUM
 */
// eslint-disable-next-line import/prefer-default-export
export function useCloudWatchRum() {
  /**
   * Reactive reference to the AWS RUM (Real User Monitoring) instance.
   * @type {import('vue').Ref<import('aws-rum-web').Orchestration|null>}
   */
  const awsRum = ref(null);
  const requestTimes = ref(new Map());
  const { config } = useDrupalSettings();

  // AwsRum's "disableAutoPageView" is set to false by default,
  // so page views are already automatically recorded.
  // This function allows us to add custom metadata to the page view.
  const recordPageView = (attributes = {}, tags = []) => {
    try {
      awsRum.value.recordPageView({
        pageId: window.location.pathname,
        pageTags: [...tags],
        pageAttributes: { ...attributes },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error recording metadata:", error);
    }
  };

  /**
   * Records a custom event with AWS RUM if the RUM instance is initialized.
   *
   * @param {string} eventName - The name of the custom event to record.
   * @param {Object} payload - The Axios request configuration or error associated with the event.
   */
  const fireCustomEvent = (eventName, payload) => {
    if (awsRum.value) {
      awsRum.value.recordEvent(eventName, {
        configOrError: payload,
      });
    }
  };

  const requestIdForConfig = (config) => `${config.method}${config.url}`;

  /**
   * Records the start time of an Axios request using a unique identifier based on the request configuration.
   *
   * @param {import('axios').AxiosRequestConfig} config - The Axios request configuration object.
   */
  const recordTime = (config) => {
    requestTimes.value.set(requestIdForConfig(config), Date.now());
  };

  /**
   * Calculates elapsed time information from the given Axios request config if available.
   *
   * @param {import('axios').AxiosResponse} response - The Axios response object.
   * @returns {import('axios').AxiosResponse} The original response object, potentially with timing information processed.
   */
  const calculateElapsedTime = (response) => {
    let time = -1;
    try {
      if (response?.config) {
        const requestId = requestIdForConfig(response.config);
        const start = requestTimes.value.get(requestId);
        time = start ? Date.now() - start : -1;
        requestTimes.value.delete(requestId);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error calculating request timing:", error);
    }
    return { ...response, time };
  };

  const initializeRum = useMemoize(async () => {
    const notAdmin = config?.currentUser && parseInt(config.currentUser) > 1;
    if (notAdmin) {
      try {
        await restClient
          .get({ path: "/performance-config", queries: {} })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error("Error fetching performance config:", error);
            return null;
          })
          .then((data) => {
            const rumConfig = {
              sessionSampleRate: data.sessionSampleRate,
              identityPoolId: data.identityPoolId,
              endpoint: data.endpoint,
              telemetries: [
                [
                  ...data.telemetries,
                  { eventLimit: 0, recordAllTypes: ["document", "other"] },
                ],
              ],
              allowCookies: data.allowCookies,
              enableXRay: data.enableXRay,
              signing: data.signing,
              sessionAttributes: {
                uid: config?.currentUser,
              },
              disableAutoPageView: true,
            };
            const rumInstance = new AwsRum(
              data.APPLICATION_ID,
              data.APPLICATION_VERSION,
              data.APPLICATION_REGION,
              rumConfig,
            );
            awsRum.value = rumInstance;
            if (config.nodeType) {
              recordPageView({ nodeType: config.nodeType }, [config.nodeType]);
            } else {
              recordPageView();
            }
            // Add interceptor for Axios requests.
            axios.interceptors.request.use(
              (requestConfig) => {
                const { url, method, data: requestData } = requestConfig;
                recordTime(requestConfig);
                fireCustomEvent("api_request", { url, method, requestData });
                return requestConfig;
              },
              (error) => Promise.reject(error),
            );
            // Add interceptor for Axios responses, including timing.
            axios.interceptors.response.use(
              (response) => {
                try {
                  response = calculateElapsedTime(response);
                  const {
                    data: responseData,
                    status,
                    config: requestConfig,
                    time,
                  } = response;
                  const { url, method, data: requestData } = requestConfig;
                  fireCustomEvent("api_response", {
                    url,
                    method,
                    responseData,
                    status,
                    requestData,
                    time,
                  });
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error("Error firing response event:", error);
                }
                return response;
              },
              (error) => {
                fireCustomEvent("api_error", {
                  columnNumber: error.columnNumber,
                  lineNumber: error.lineNumber,
                  fileName: error.fileName,
                  message: error.message,
                });
                return Promise.reject(error);
              },
            );
            return awsRum;
          });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error initializing AWS RUM:", error);
      }
    }
  });

  return {
    awsRum,
    initializeRum,
    recordPageView,
  };
}
